import type { FormEvent, ReactElement } from 'react';
import { useState } from 'react';
import { useRenameTopic } from '../api/queries';
import { Button, Field, Modal, TextArea, useNotices } from '../ui';

/**
 * A person's own name for their thread. Vladimir, 2026-09-24: *"Add options to
 * rename topics"*. What it is matched by stays what it is about; the name is
 * what a person reads on the page and in the list.
 */
export function RenameTopic({
  id,
  name,
  description,
  onClose,
}: {
  id: string;
  name: string;
  description: string | null;
  onClose: () => void;
}): ReactElement {
  const rename = useRenameTopic();
  const { show } = useNotices();
  const [next, setNext] = useState(name);
  const [about, setAbout] = useState(description ?? '');
  const ready = next.trim().length >= 2;
  const save = (e?: FormEvent) => {
    e?.preventDefault();
    if (!ready) return;
    rename.mutate(
      { id, body: { name: next.trim(), description: about.trim() || null } },
      {
        onSuccess: () => {
          onClose();
          show({ tone: 'success', text: `Renamed “${next.trim()}”.` });
        },
        onError: (err) => show({ tone: 'failure', text: err.message }),
      },
    );
  };
  return (
    <Modal
      title="Rename the topic"
      onClose={onClose}
      foot={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!ready}
            working={rename.isPending ? 'Saving…' : false}
            onClick={() => save()}
          >
            Save
          </Button>
        </>
      }
    >
      <form className="stack" style={{ gap: 12 }} onSubmit={save}>
        <Field
          label="Name"
          value={next}
          maxLength={80}
          autoFocus
          onChange={(e) => setNext(e.target.value)}
        />
        <TextArea
          label="What it is about"
          value={about}
          maxLength={300}
          rows={3}
          onChange={(e) => setAbout(e.target.value)}
        />
      </form>
    </Modal>
  );
}
